import type { AccountUser, ExchangeUserPermission } from '../../types/exchange'
import { apiRequest } from './client'

export interface OrgMembership {
  id: string
  orgId: string
  userId: string
  accountId: string | null
  role: 'org_owner' | 'org_admin' | 'org_staff'
  title: string | null
  active: boolean
  createdAt: string
  updatedAt: string
}

interface BackendPermissionsResponse {
  orgMemberships: OrgMembership[]
  exchangeParticipations: unknown[]
  exchangePermissions: ExchangeUserPermission[]
  isPlatformAdmin: boolean
}

const ORG_ROLE_TO_ACCOUNT_ROLE: Record<OrgMembership['role'], AccountUser['role']> = {
  org_owner: 'owner',
  org_admin: 'admin',
  org_staff: 'member',
}

function orgMembershipToAccountUser(membership: OrgMembership): AccountUser {
  return {
    id: membership.id,
    accountId: membership.accountId ?? membership.orgId,
    userId: membership.userId,
    role: ORG_ROLE_TO_ACCOUNT_ROLE[membership.role] ?? 'member',
    createdAt: membership.createdAt,
    updatedAt: membership.updatedAt,
  }
}

export interface PermissionsResponse {
  accountUsers: AccountUser[]
  exchangePermissions: ExchangeUserPermission[]
  orgMemberships: OrgMembership[]
  isPlatformAdmin: boolean
}

async function fetchAndMap(): Promise<PermissionsResponse> {
  const data = await apiRequest<BackendPermissionsResponse>('/permissions')
  return {
    accountUsers: (data.orgMemberships ?? []).map(orgMembershipToAccountUser),
    exchangePermissions: data.exchangePermissions ?? [],
    orgMemberships: data.orgMemberships ?? [],
    isPlatformAdmin: data.isPlatformAdmin ?? false,
  }
}

export const permissionsApi = {
  async getPermissions(): Promise<PermissionsResponse> {
    return fetchAndMap()
  },

  async getAccountRolesForUser(_userId: string): Promise<AccountUser[]> {
    const data = await fetchAndMap()
    return data.accountUsers
  },

  async getAllExchangePermissionsForUser(_userId: string): Promise<ExchangeUserPermission[]> {
    const data = await fetchAndMap()
    return data.exchangePermissions
  },

  async getResolvedPermissions(exchangeId: string) {
    return apiRequest(`/permissions/resolve/${exchangeId}`)
  },

  async getAllResolvedPermissions() {
    return apiRequest('/permissions/resolve')
  },
}
